import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Check, X, ArrowUp, ArrowDown, LayoutGrid, ChevronsUpDown, Copy } from 'lucide-react';
import { ICON_MAP, ICON_OPTIONS, ICON_KEYWORDS, PAGE_REGISTRY, BUILTIN_DROPDOWNS, BUILTIN_ACTIONS, ACTION_GROUPS } from '../../utils/navHeaderRegistry';
import UserSpacesPanel from './UserSpacesPanel';
import { useNavLinkStyles, linkStyleKey, defaultColorFor, LINK_COLOR_CHOICES, NAV_STYLES_CHANGED } from '../../utils/navLinkStyles';

const IconPreview = ({ name }) => {
  const IconComp = ICON_MAP[name] || ICON_MAP.Link2;
  return <IconComp className="w-4 h-4 text-orange-600" />;
};

// ── Generic searchable dropdown (Popover + cmdk) — used everywhere the plain
// <Select> would otherwise force scrolling through a long, unfiltered list.
const SearchableSelect = ({ options, value, onChange, placeholder = 'Select...', testId, showIcon = false }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm"
        >
          <span className="flex items-center gap-2 truncate">
            {showIcon && selected && <IconPreview name={selected.icon} />}
            {selected?.label || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 bg-white" align="start">
        <Command>
          <CommandInput placeholder="Search..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {options.map(o => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  keywords={o.keywords}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                  {showIcon && <IconPreview name={o.icon} />}
                  <span className="ml-1 truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const ICON_SELECT_OPTIONS = ICON_OPTIONS.map(name => ({ value: name, label: name, icon: name, keywords: (ICON_KEYWORDS[name] || '').split(' ').filter(Boolean) }));

const IconPicker = ({ value, onChange }) => (
  <SearchableSelect options={ICON_SELECT_OPTIONS} value={value} onChange={onChange} placeholder="Choose icon" testId="nav-header-icon-picker" showIcon />
);

// ── Add Link dialog — 4 ways to add a header item ──────────────────────────
const AddLinkDialog = ({ open, onClose, onAdd }) => {
  const [tab, setTab] = useState('page');
  const [pageRoute, setPageRoute] = useState(PAGE_REGISTRY[0].route);
  const [pageLabel, setPageLabel] = useState('');
  const [pageIcon, setPageIcon] = useState(PAGE_REGISTRY[0].icon);
  const [builtinKey, setBuiltinKey] = useState(BUILTIN_DROPDOWNS[0].builtin_key);
  const [actionKey, setActionKey] = useState(BUILTIN_ACTIONS[0].key);
  const [actionLabel, setActionLabel] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customIcon, setCustomIcon] = useState('Link2');
  const [customUrl, setCustomUrl] = useState('');
  const [ddLabel, setDdLabel] = useState('');
  const [ddIcon, setDdIcon] = useState('Link2');
  const [children, setChildren] = useState([]);
  const [childLabel, setChildLabel] = useState('');
  const [childIcon, setChildIcon] = useState('Link2');
  const [childTarget, setChildTarget] = useState(PAGE_REGISTRY[0].route);
  const [childIsUrl, setChildIsUrl] = useState(false);

  const reset = () => {
    setTab('page'); setPageRoute(PAGE_REGISTRY[0].route); setPageLabel(''); setPageIcon(PAGE_REGISTRY[0].icon);
    setBuiltinKey(BUILTIN_DROPDOWNS[0].builtin_key); setActionKey(BUILTIN_ACTIONS[0].key); setActionLabel(''); setCustomLabel(''); setCustomIcon('Link2'); setCustomUrl('');
    setDdLabel(''); setDdIcon('Link2'); setChildren([]); setChildLabel(''); setChildIcon('Link2');
    setChildTarget(PAGE_REGISTRY[0].route); setChildIsUrl(false);
  };

  const addChild = () => {
    if (!childLabel.trim()) { toast.error('Child link needs a name'); return; }
    setChildren(c => [...c, {
      id: `tmp_${Date.now()}_${c.length}`,
      label: childLabel.trim(),
      icon: childIcon,
      route: childIsUrl ? null : childTarget,
      url: childIsUrl ? childTarget : null,
    }]);
    setChildLabel(''); setChildTarget(PAGE_REGISTRY[0].route);
  };

  const submit = () => {
    if (tab === 'page') {
      const page = PAGE_REGISTRY.find(p => p.route === pageRoute);
      onAdd({ type: 'page', label: pageLabel.trim() || page.label, icon: pageIcon, route: page.route, permission_key: page.permission_key });
    } else if (tab === 'builtin') {
      const b = BUILTIN_DROPDOWNS.find(x => x.builtin_key === builtinKey);
      onAdd({ type: 'builtin_dropdown', label: b.label, icon: b.icon, builtin_key: b.builtin_key, permission_key: b.permission_key });
    } else if (tab === 'builtin_action') {
      const a = BUILTIN_ACTIONS.find(x => x.key === actionKey);
      onAdd({ type: 'builtin_action', label: actionLabel.trim() || a.label, icon: a.icon, builtin_key: a.key, permission_key: a.permission_key || null });
    } else if (tab === 'custom_link') {
      if (!customLabel.trim() || !customUrl.trim()) { toast.error('Name and URL are required'); return; }
      onAdd({ type: 'custom_link', label: customLabel.trim(), icon: customIcon, url: customUrl.trim() });
    } else if (tab === 'custom_dropdown') {
      if (!ddLabel.trim()) { toast.error('Dropdown needs a name'); return; }
      if (!children.length) { toast.error('Add at least one link inside the dropdown'); return; }
      onAdd({ type: 'custom_dropdown', label: ddLabel.trim(), icon: ddIcon, children });
    }
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="nav-header-add-link-dialog">
        <DialogHeader><DialogTitle>Add Header Link</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="page" data-testid="add-link-tab-page">Page</TabsTrigger>
            <TabsTrigger value="builtin" data-testid="add-link-tab-builtin">Dropdown</TabsTrigger>
            <TabsTrigger value="builtin_action" data-testid="add-link-tab-action">Single link</TabsTrigger>
            <TabsTrigger value="custom_link" data-testid="add-link-tab-custom-link">Custom Link</TabsTrigger>
            <TabsTrigger value="custom_dropdown" data-testid="add-link-tab-custom-dropdown">Custom Dropdown</TabsTrigger>
          </TabsList>

          <TabsContent value="page" className="space-y-3 pt-2">
            <div>
              <Label>Page</Label>
              <SearchableSelect
                options={PAGE_REGISTRY.map(p => ({ value: p.route, label: p.label, icon: p.icon }))}
                value={pageRoute}
                onChange={(v) => { setPageRoute(v); const p = PAGE_REGISTRY.find(x => x.route === v); if (p) setPageIcon(p.icon); }}
                placeholder="Search pages..."
                testId="add-link-page-select"
                showIcon
              />
            </div>
            <div><Label>Display Name (optional override)</Label><Input value={pageLabel} onChange={e => setPageLabel(e.target.value)} placeholder={PAGE_REGISTRY.find(p => p.route === pageRoute)?.label} data-testid="add-link-page-label-input" /></div>
            <div><Label>Icon</Label><IconPicker value={pageIcon} onChange={setPageIcon} /></div>
          </TabsContent>

          <TabsContent value="builtin" className="space-y-3 pt-2">
            <div>
              <Label>Existing Header Dropdown</Label>
              <SearchableSelect
                options={BUILTIN_DROPDOWNS.map(b => ({ value: b.builtin_key, label: b.label, icon: b.icon }))}
                value={builtinKey}
                onChange={setBuiltinKey}
                placeholder="Search dropdowns..."
                testId="add-link-builtin-select"
                showIcon
              />
            </div>
            <p className="text-xs text-gray-500">Renders the real, unmodified {BUILTIN_DROPDOWNS.find(b => b.builtin_key === builtinKey)?.label} dropdown with all of its existing behaviour.</p>
          </TabsContent>

          <TabsContent value="builtin_action" className="space-y-3 pt-2">
            <div>
              <Label>An item from one of the drop-downs</Label>
              <SearchableSelect
                options={BUILTIN_ACTIONS.map(a => ({ value: a.key, label: `${ACTION_GROUPS[a.group]}: ${a.label}`, icon: a.icon, keywords: [a.label, ACTION_GROUPS[a.group]] }))}
                value={actionKey}
                onChange={(v) => { setActionKey(v); setActionLabel(''); }}
                placeholder="Search..."
                testId="add-link-action-select"
                showIcon
              />
            </div>
            <div><Label>Display Name (optional override)</Label><Input value={actionLabel} onChange={e => setActionLabel(e.target.value)} placeholder={BUILTIN_ACTIONS.find(a => a.key === actionKey)?.label} data-testid="add-link-action-label-input" /></div>
            <p className="text-xs text-gray-500">Becomes a link of its own on the side bar. It does exactly what it does inside {ACTION_GROUPS[BUILTIN_ACTIONS.find(a => a.key === actionKey)?.group]}, and is only shown to people who are allowed to use it. Its icon and colour can be changed afterwards with the pencil.</p>
          </TabsContent>

          <TabsContent value="custom_link" className="space-y-3 pt-2">
            <div><Label>Name</Label><Input value={customLabel} onChange={e => setCustomLabel(e.target.value)} data-testid="add-link-custom-name-input" /></div>
            <div><Label>Icon</Label><IconPicker value={customIcon} onChange={setCustomIcon} /></div>
            <div><Label>URL</Label><Input value={customUrl} onChange={e => setCustomUrl(e.target.value)} placeholder="https://..." data-testid="add-link-custom-url-input" /></div>
          </TabsContent>

          <TabsContent value="custom_dropdown" className="space-y-3 pt-2">
            <div><Label>Dropdown Name</Label><Input value={ddLabel} onChange={e => setDdLabel(e.target.value)} data-testid="add-link-dropdown-name-input" /></div>
            <div><Label>Icon</Label><IconPicker value={ddIcon} onChange={setDdIcon} /></div>
            <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Links inside this dropdown</Label>
              {children.map((c, idx) => (
                <div key={c.id} className="flex items-center justify-between bg-white rounded-md px-2 py-1.5 border text-sm" data-testid={`add-link-dropdown-child-${idx}`}>
                  <span className="flex items-center gap-2"><IconPreview name={c.icon} /> {c.label}</span>
                  <button type="button" onClick={() => setChildren(cs => cs.filter((_, i) => i !== idx))} data-testid={`add-link-dropdown-child-remove-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <Input value={childLabel} onChange={e => setChildLabel(e.target.value)} placeholder="Link name" data-testid="add-link-dropdown-child-name-input" />
                <IconPicker value={childIcon} onChange={setChildIcon} />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setChildIsUrl(false)} className={`text-xs px-2 py-1 rounded ${!childIsUrl ? 'bg-orange-500 text-white' : 'bg-gray-200'}`} data-testid="add-link-dropdown-child-mode-page">Page</button>
                <button type="button" onClick={() => setChildIsUrl(true)} className={`text-xs px-2 py-1 rounded ${childIsUrl ? 'bg-orange-500 text-white' : 'bg-gray-200'}`} data-testid="add-link-dropdown-child-mode-url">Custom URL</button>
                {!childIsUrl ? (
                  <div className="flex-1">
                    <SearchableSelect
                      options={PAGE_REGISTRY.map(p => ({ value: p.route, label: p.label, icon: p.icon }))}
                      value={childTarget}
                      onChange={setChildTarget}
                      placeholder="Search pages..."
                      testId="add-link-dropdown-child-page-select"
                      showIcon
                    />
                  </div>
                ) : (
                  <Input className="flex-1" value={childTarget} onChange={e => setChildTarget(e.target.value)} placeholder="https://..." data-testid="add-link-dropdown-child-url-input" />
                )}
                <Button type="button" size="sm" onClick={addChild} data-testid="add-link-dropdown-child-add-btn"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="add-link-cancel-btn">Cancel</Button>
          <Button onClick={submit} data-testid="add-link-submit-btn">Add Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── One row in the sector's link list ───────────────────────────────────────
// The dash behind an icon: the Encon slash, leaning right, nearly filling the square.
const SLASH_BG = (color) => `linear-gradient(105deg, transparent 0 14%, ${color} 14% 86%, transparent 86%)`;

// A link's icon and colour as they look on the side bar: a dark rounded square, a white border, a white icon and a dash of the link's colour behind it.
const LinkTile = ({ item, style }) => {
  const Icon = ICON_MAP[style?.icon || item.icon] || ICON_MAP.Link2;
  const color = style?.color || defaultColorFor(item);
  return (
    <span
      className="inline-flex items-center justify-center shrink-0"
      style={{ width: 30, height: 30, borderRadius: 8, border: '1.5px solid #ffffff', boxShadow: '0 0 0 1px #d1d5db', backgroundColor: '#1d1d1b', backgroundImage: SLASH_BG(color) }}
      data-testid="nav-link-tile"
    >
      <Icon className="w-4 h-4" style={{ color: '#ffffff' }} />
    </span>
  );
};

// ── Edit a link's icon and colour ───────────────────────────────────────────
// What is chosen here belongs to the LINK, not to this sector's copy of it: the same link has the same icon and colour in every sector, every branch
// and every person's side bar.
const LinkStyleDialog = ({ link, current, onClose, onSave, onReset }) => {
  const [icon, setIcon] = useState('Link2');
  const [color, setColor] = useState('#64748b');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!link) return;
    setIcon(current?.icon || link.icon || 'Link2');
    setColor(current?.color || defaultColorFor(link));
  }, [link, current]);

  if (!link) return null;
  const validColor = /^#[0-9a-fA-F]{6}$/.test(color);
  const save = async () => {
    if (!validColor) { toast.error('Choose a colour first'); return; }
    setSaving(true);
    await onSave(link, icon, color);
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Choosing a colour in the browser's own colour picker, or an icon in the list, happens outside this window: that must never close it and lose the choice. */}
      <DialogContent className="max-w-md" data-testid="nav-link-style-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Edit link: {link.label}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">The icon and colour you choose belong to this link everywhere it appears: every sector, every branch and every person's side bar.</p>
        <div className="space-y-4">
          <div><Label>Icon</Label><IconPicker value={icon} onChange={setIcon} /></div>
          <div>
            <Label>Colour</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="nav-link-style-swatches">
              {LINK_COLOR_CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  title={choice.name}
                  onClick={() => setColor(choice.value)}
                  className="h-7 w-7 rounded-md"
                  style={{ backgroundColor: choice.value, outline: color.toLowerCase() === choice.value ? '2px solid #1d1d1b' : '1px solid #d1d5db', outlineOffset: 1 }}
                  data-testid={`nav-link-style-swatch-${choice.value.slice(1)}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input type="color" value={validColor ? color : '#64748b'} onChange={(e) => setColor(e.target.value)} className="h-8 w-10 rounded border" data-testid="nav-link-style-color-input" />
              <Input className="h-8 w-28 font-mono text-xs" value={color} onChange={(e) => setColor(e.target.value)} maxLength={7} data-testid="nav-link-style-hex-input" />
              <span className="text-xs text-gray-500">How it looks:</span>
              <LinkTile item={link} style={{ icon, color: validColor ? color : defaultColorFor(link) }} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onReset(link)} data-testid="nav-link-style-reset-btn">Reset to default</Button>
          <Button variant="outline" onClick={onClose} data-testid="nav-link-style-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="nav-link-style-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LinkRow = ({ item, index, total, style, onMove, onRemove, onEdit }) => (
  <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2" data-testid={`nav-header-link-row-${item.id}`}>
    <div className="flex items-center gap-2 min-w-0">
      <LinkTile item={item} style={style} />
      <span className="font-medium text-sm truncate">{item.label}</span>
      <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">
        {item.type === 'builtin_dropdown' ? 'Dropdown' : item.type === 'builtin_action' ? 'Single link' : item.type === 'custom_dropdown' ? 'Custom Dropdown' : item.type === 'custom_link' ? 'Custom Link' : 'Page'}
      </span>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <button type="button" onClick={() => onEdit(item)} className="p-1" title="Change this link's icon and colour" data-testid={`nav-header-link-edit-${item.id}`}><Pencil className="w-3.5 h-3.5 text-gray-600" /></button>
      <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="p-1 disabled:opacity-30" data-testid={`nav-header-link-up-${item.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="p-1 disabled:opacity-30" data-testid={`nav-header-link-down-${item.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => onRemove(index)} className="p-1" data-testid={`nav-header-link-remove-${item.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
    </div>
  </div>
);

// The dashboards a Space can go with. They are part of the app (so they travel with every deployment); 'default' is the home page as it stands.
const DASHBOARD_VIEWS = [
  { value: 'default', label: 'Default Space (the current home page)' },
  { value: 'sales', label: 'Sales Space' },
  { value: 'admin', label: 'Admin Space' },
];

const ManageGlobalHeaderSettings = () => {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [sectors, setSectors] = useState([]);
  const [sectorId, setSectorId] = useState(null);
  const [sectorDetail, setSectorDetail] = useState(null);
  const [newSectorName, setNewSectorName] = useState('');
  const [copyFromId, setCopyFromId] = useState(''); // the Space a new Space starts as a copy of ('' = start empty)
  const newSectorInputRef = useRef(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [groupSectors, setGroupSectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const { styles: linkStyles, reload: reloadLinkStyles } = useNavLinkStyles();
  const [editingLink, setEditingLink] = useState(null);

  useEffect(() => {
    api.get(`${API}/branches`).then(r => {
      const list = r.data?.branches || [];
      setBranches(list);
      if (list.length) setBranchId(list[0].id);
    }).catch(() => {});
  }, []);

  const loadSectors = useCallback(() => {
    if (!branchId) return;
    api.get(`${API}/nav-header/sectors`, { params: { branch_id: branchId } }).then(r => {
      const list = r.data?.sectors || [];
      setSectors(list);
      setSectorId(prev => (list.some(s => s.id === prev) ? prev : (list.find(s => s.is_default)?.id || list[0]?.id || null)));
    }).catch(() => {});
  }, [branchId]);

  const loadGroupSectors = useCallback(() => {
    if (!branchId) return;
    api.get(`${API}/nav-header/group-sectors`, { params: { branch_id: branchId } })
      .then(r => setGroupSectors(r.data?.groups || []))
      .catch(() => {});
  }, [branchId]);

  useEffect(() => { loadSectors(); loadGroupSectors(); }, [loadSectors, loadGroupSectors]);

  useEffect(() => {
    if (!sectorId) { setSectorDetail(null); return; }
    api.get(`${API}/nav-header/sectors/${sectorId}`).then(r => setSectorDetail(r.data)).catch(() => setSectorDetail(null));
  }, [sectorId]);

  const createSector = async () => {
    if (!newSectorName.trim()) { toast.error('Sector name is required'); return; }
    setBusy(true);
    try {
      const r = await api.post(`${API}/nav-header/sectors`, { branch_id: branchId, name: newSectorName.trim() });
      // Starting as a copy of another Space: it gets that Space's links (each with its own id) and its dashboard. It is still a new Space with its own name, and
      // changing it afterwards never changes the one it was copied from.
      let copiedFrom = null;
      if (copyFromId) {
        try {
          const source = (await api.get(`${API}/nav-header/sectors/${copyFromId}`)).data;
          const stamp = Date.now().toString(36);
          const links = (source.links || []).map((link, i) => ({ ...link, id: `${stamp}-${i}-${Math.random().toString(36).slice(2, 8)}` }));
          if (links.length > 0) await api.put(`${API}/nav-header/sectors/${r.data.id}/links`, { links });
          if (source.dashboard_view && source.dashboard_view !== 'default') await api.put(`${API}/nav-header/sectors/${r.data.id}/dashboard-view`, { dashboard_view: source.dashboard_view });
          copiedFrom = source.name;
        } catch (copyError) {
          toast.error('The new Space was created, but its links could not be copied across. You can add them by hand.');
        }
      }
      setNewSectorName('');
      setCopyFromId('');
      toast.success(copiedFrom ? `Sector "${r.data.name}" created as a copy of "${copiedFrom}"` : `Sector "${r.data.name}" created`);
      loadSectors();
      setSectorId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create sector');
    } finally { setBusy(false); }
  };

  const renameSector = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await api.put(`${API}/nav-header/sectors/${id}`, { name: renameValue.trim() });
      toast.success('Sector renamed');
      setRenamingId(null);
      loadSectors();
    } catch (e) { toast.error('Failed to rename sector'); }
  };

  const deleteSector = async (s) => {
    if (s.is_default) return;
    if (!window.confirm(`Delete sector "${s.name}"? Business Unit Groups mapped to it will fall back to Default.`)) return;
    try {
      await api.delete(`${API}/nav-header/sectors/${s.id}`);
      toast.success('Sector deleted');
      if (sectorId === s.id) setSectorId(null);
      loadSectors();
      loadGroupSectors();
    } catch (e) { toast.error('Failed to delete sector'); }
  };

  const saveLinks = async (newLinks) => {
    setSectorDetail(d => ({ ...d, links: newLinks }));
    try {
      await api.put(`${API}/nav-header/sectors/${sectorId}/links`, { links: newLinks });
    } catch (e) {
      toast.error('Failed to save links');
    }
  };

  // A link's icon and colour belong to the link itself (not to this sector's copy of it), so they change everywhere that link appears.
  const saveLinkStyle = async (link, icon, color) => {
    try {
      await api.put(`${API}/nav-header/link-styles`, { key: linkStyleKey(link), icon, color });
      toast.success(`${link.label} updated everywhere it appears`);
      reloadLinkStyles();
      window.dispatchEvent(new Event(NAV_STYLES_CHANGED));
      setEditingLink(null);
    } catch (e) {
      // Say what actually went wrong (the server's reason and its error number), not just that something did.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(`Could not save the icon and colour${status ? ` (error ${status})` : ''}: ${typeof detail === 'string' ? detail : (e?.message || 'no reply from the server')}`);
    }
  };

  const resetLinkStyle = async (link) => {
    try {
      await api.delete(`${API}/nav-header/link-styles`, { params: { key: linkStyleKey(link) } });
      toast.success(`${link.label} is back to its default icon and colour`);
      reloadLinkStyles();
      window.dispatchEvent(new Event(NAV_STYLES_CHANGED));
      setEditingLink(null);
    } catch (e) {
      toast.error('Failed to reset the link');
    }
  };

  const addLink = (item) => saveLinks([...(sectorDetail?.links || []), item]);
  const removeLink = (idx) => saveLinks((sectorDetail?.links || []).filter((_, i) => i !== idx));
  const moveLink = (idx, dir) => {
    const links = [...(sectorDetail?.links || [])];
    const target = idx + dir;
    if (target < 0 || target >= links.length) return;
    [links[idx], links[target]] = [links[target], links[idx]];
    saveLinks(links);
  };

  // The dashboard that goes with this Space (Default Space / Sales Space / Admin Space).
  const saveDashboardView = async (view) => {
    try {
      await api.put(`${API}/nav-header/sectors/${sectorId}/dashboard-view`, { dashboard_view: view });
      setSectorDetail((d) => ({ ...d, dashboard_view: view }));
      toast.success('Dashboard saved for this Space');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the dashboard');
    }
  };

  const setGroupSector = async (group, newSectorIdVal) => {
    try {
      await api.put(`${API}/nav-header/group-sectors`, { branch_id: branchId, group, sector_id: newSectorIdVal || null });
      setGroupSectors(gs => gs.map(g => g.group === group ? { ...g, sector_id: newSectorIdVal || null } : g));
      toast.success('Business Unit Group updated');
    } catch (e) { toast.error('Failed to update assignment'); }
  };

  return (
    <div className="space-y-6" data-testid="manage-global-header-settings">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)] items-start" data-testid="user-spaces-columns">
      <div className="space-y-6 min-w-0" data-testid="user-spaces-left">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-orange-500" /> User Spaces</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-500">Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="h-8 w-48" data-testid="nav-header-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name || b.id} ({b.id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500">Sectors for this branch</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {sectors.map(s => (
                <div key={s.id} className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 ${s.id === sectorId ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`} data-testid={`nav-header-sector-tab-${s.id}`}>
                  {renamingId === s.id ? (
                    <>
                      <Input className="h-6 w-28 text-xs" value={renameValue} onChange={e => setRenameValue(e.target.value)} data-testid={`nav-header-sector-rename-input-${s.id}`} />
                      <button onClick={() => renameSector(s.id)} data-testid={`nav-header-sector-rename-save-${s.id}`}><Check className="w-3.5 h-3.5 text-green-600" /></button>
                      <button onClick={() => setRenamingId(null)}><X className="w-3.5 h-3.5 text-gray-500" /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setSectorId(s.id)} className="text-sm font-medium" data-testid={`nav-header-sector-select-${s.id}`}>
                        {s.name}{s.is_default ? ' (Default)' : ''}
                      </button>
                      <button onClick={() => { setRenamingId(s.id); setRenameValue(s.name); }} data-testid={`nav-header-sector-rename-btn-${s.id}`}><Pencil className="w-3 h-3 text-gray-400" /></button>
                      <button onClick={() => { setCopyFromId(s.id); setNewSectorName(`${s.name} copy`); setTimeout(() => { if (newSectorInputRef.current) { newSectorInputRef.current.focus(); newSectorInputRef.current.select(); } }, 0); }} title="Copy this Space: name the copy and add it" data-testid={`nav-header-sector-copy-btn-${s.id}`}><Copy className="w-3 h-3 text-gray-400" /></button>
                      {!s.is_default && (
                        <button onClick={() => deleteSector(s)} data-testid={`nav-header-sector-delete-btn-${s.id}`}><Trash2 className="w-3 h-3 text-red-400" /></button>
                      )}
                    </>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-1.5">
                <Select value={copyFromId || '__none__'} onValueChange={(v) => setCopyFromId(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="nav-header-copy-from-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__none__">Start empty</SelectItem>
                    {sectors.map(s => <SelectItem key={s.id} value={s.id}>Copy of {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input ref={newSectorInputRef} className="h-8 w-40" placeholder="New sector name" value={newSectorName} onChange={e => setNewSectorName(e.target.value)} data-testid="nav-header-new-sector-input" />
                <Button size="sm" disabled={busy} onClick={createSector} data-testid="nav-header-new-sector-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Sector</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {sectorDetail && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Links — {sectorDetail.name}</CardTitle>
            <div className="flex items-center gap-2 pt-1" data-testid="nav-header-dashboard-view-row">
              <Label className="text-xs whitespace-nowrap">Dashboard for this Space</Label>
              <Select value={sectorDetail.dashboard_view || 'default'} onValueChange={saveDashboardView}>
                <SelectTrigger className="w-72 h-8" data-testid="nav-header-dashboard-view-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {DASHBOARD_VIEWS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <CardDescription>Order shown here is the order shown on the left side bar, top to bottom. Use the pencil to change a link's icon and colour; it changes everywhere that link appears.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(sectorDetail.links || []).length === 0 && (
              <p className="text-sm text-gray-400" data-testid="nav-header-no-links">No links yet — add one below.</p>
            )}
            {(sectorDetail.links || []).map((item, idx) => (
              <LinkRow key={item.id} item={item} index={idx} total={sectorDetail.links.length} style={linkStyles[linkStyleKey(item)]} onMove={moveLink} onRemove={removeLink} onEdit={setEditingLink} />
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setAddLinkOpen(true)} data-testid="nav-header-add-link-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
            </Button>
          </CardContent>
        </Card>
      )}
      </div>

      <div className="space-y-6 min-w-0" data-testid="user-spaces-right">
      <UserSpacesPanel branchId={branchId} sectors={sectors} onImported={() => { loadSectors(); loadGroupSectors(); }} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assign Business Unit Groups to Sectors</CardTitle>
          <CardDescription>
            Sets each Business Unit Group's default Sector for this branch. Assign users to a group in
            Settings &gt; User Admin &gt; Business Unit Groups — everyone in that group then defaults to
            this Sector (they can still switch sectors themselves from the header).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {groupSectors.map(g => (
            <div key={g.group} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`nav-header-group-row-${g.group}`}>
              <span className="text-sm font-medium">{g.label}</span>
              <Select value={g.sector_id || ''} onValueChange={(v) => setGroupSector(g.group, v)}>
                <SelectTrigger className="w-48" data-testid={`nav-header-group-sector-select-${g.group}`}><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {sectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </CardContent>
      </Card>
      </div>
      </div>

      <AddLinkDialog open={addLinkOpen} onClose={() => setAddLinkOpen(false)} onAdd={addLink} />
      <LinkStyleDialog link={editingLink} current={editingLink ? linkStyles[linkStyleKey(editingLink)] : null} onClose={() => setEditingLink(null)} onSave={saveLinkStyle} onReset={resetLinkStyle} />
    </div>
  );
};

export default ManageGlobalHeaderSettings;
